import { ColDef } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { Box } from '@atgfr8/atg-ui-tailwind';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { getRowGroupStyle } from '@components/Grid/utils';
import { useParams } from '@solidjs/router';
import {
  CustomerClaimHistoryItem,
  getCustomerClaimsHistory,
} from '@store/customers/customerDetails';
import { dateComparator, dateFormatter } from '@store/loadboard/utils';
import { formatAmount } from '@utils/utils';
import { atgLogo } from '@views/ltl';
import { createEffect, createSignal } from 'solid-js';

const ClaimHistory = () => {
  const params = useParams();
  const [claimHistory, setClaimHistory] = createSignal<
    CustomerClaimHistoryItem[]
  >([]);
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const fetchClaims = async () => {
    gridRef()?.api.showLoadingOverlay();
    if (params.id) {
      const res = await getCustomerClaimsHistory(params.id);
      res && setClaimHistory(res);
    }
    gridRef()?.api.hideOverlay();
  };

  let previousId = params.id;
  createEffect(async () => {
    if (previousId !== params.id) {
      previousId = params.id;
      await fetchClaims();
    }
  });

  const cellStyle = {
    padding: '10px',
    'word-break': 'break-word',
    'white-space': 'normal',
    'line-height': '1.6 ',
  };

  const columnDefs: ColDef[] = [
    {
      field: 'loadNumber',
      headerName: 'Load#',
      minWidth: 120,
      filter: 'agTextColumnFilter',
      cellStyle,
    },
    {
      field: 'carrier',
      headerName: 'Carrier',
      minWidth: 300,
      filter: 'agTextColumnFilter',
      cellStyle,
    },
    {
      field: 'agent',
      headerName: 'Agent',
      rowGroup: true,
      hide: true,
      cellStyle,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'claimDate',
      headerName: 'Claim Date',
      valueFormatter: dateFormatter,
      minWidth: 120,
      filter: 'agDateColumnFilter',
      filterParams: {
        suppressAndOrCondition: true,
        buttons: ['apply', 'reset'],
        comparator: dateComparator,
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      cellStyle,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      filter: 'agTextColumnFilter',
      cellStyle,
    },
    {
      field: 'value',
      headerName: 'Value',
      minWidth: 150,
      cellStyle,
      valueFormatter: (params) => {
        const value = params.value as number | undefined | null;
        return value !== undefined && value !== null ? formatAmount(value) : '';
      },
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 350,
      flex: 1,
      autoHeight: true,
      cellStyle,
      filter: 'agTextColumnFilter',
    },
  ];
  return (
    <Box class="ag-theme-alpine ag-theme-custom-border flex flex-col my-row-group-border custom-ag-row-icon-grid flex-1! min-h-[400px] h-[calc(100vh-210px)]">
      <AgGridSolid
        overlayNoRowsTemplate="No data available"
        overlayLoadingTemplate={atgLogo}
        columnDefs={columnDefs}
        rowData={claimHistory()}
        getRowStyle={getRowGroupStyle}
        ref={gridRef}
        gridOptions={{
          groupDefaultExpanded: 1,
          autoGroupColumnDef: {
            headerName: 'Agent',
            minWidth: 200,
          },
          pagination: true,
          suppressPaginationPanel: false,
          maintainColumnOrder: true,
          rowGroupPanelShow: 'never',
          groupDisplayType: 'groupRows',
          sideBar: {
            toolPanels: [columnsToolPanel, filtersToolPanel],
          },
          groupRowRendererParams: {
            innerRenderer: (params: {
              node: {
                allLeafChildren: {
                  data: CustomerClaimHistoryItem;
                }[];
                level: number;
                key: string;
              };
            }) => {
              const groupLevel = params.node.level;
              let customText;

              switch (groupLevel) {
                case 0:
                  customText = `Agent: ${params.node.key}`;
                  break;
                default:
                  customText = `Grouped by: ${params.node.key}`;
              }

              return customText;
            },
          },
          onGridReady: async (gridParams) => {
            setGridRef(gridParams);
            await fetchClaims();
          },
        }}
      />
    </Box>
  );
};

export default ClaimHistory;
